import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    MdArrowBack,
    MdBusiness,
    MdCalendarToday,
    MdInfo,
    MdEmail,
    MdPhone,
    MdLocationOn,
    MdCheckCircle,
    MdRadioButtonChecked
} from 'react-icons/md';
import axios from 'axios';
import styles from './ViewPODetail.module.css';
import Loader from '../components/common/Loader';
import { GET_PO_DETAILS_BY_PO_ID_API, GET_COMPLETED_PO_DETAILS_BY_PO_ID_API } from '../config/API';
import Swal from 'sweetalert2';

const ViewPODetail = () => {
    const { poId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [poData, setPoData] = useState(null);

    useEffect(() => {
        const fetchPODetails = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('accessToken');
                const headers = { Authorization: `Bearer ${token}` };

                // 1. Try standard API
                let response;
                try {
                    response = await axios.get(GET_PO_DETAILS_BY_PO_ID_API(poId), { headers });
                } catch (e) {
                    response = { data: { status: false } };
                }

                // 2. Fallback to Completed PO details
                if (!response.data.status || !response.data.data) {
                    try {
                        const completedResponse = await axios.get(GET_COMPLETED_PO_DETAILS_BY_PO_ID_API(poId), { headers });
                        if (completedResponse.data.status) {
                            response = completedResponse;
                        }
                    } catch (e) {
                        console.error("Both PO detail APIs failed");
                    }
                }

                if (response.data.status) {
                    const data = response.data.data;

                    // Map Project
                    const project = data.ProjectMaster || data.Project || {};

                    // Map Vendor from RequestVendor structure
                    const reqVendor = data.RequestVendor || {};
                    const vendorObj = reqVendor.Vendor || {};

                    // Map Items
                    const itemsList = data.PurchaseOrderItems || [];
                    const formattedItems = itemsList.map(item => ({
                        id: item.id,
                        productName: item.Product?.productName || 'N/A',
                        description: item.Product?.description || item.productDescription || '',
                        size: item.size || '',
                        qty: item.qty || 0,
                        rate: item.rate || 0,
                        unit: item.Unit?.unitName || 'Nos',
                        total: item.total || (parseFloat(item.qty || 0) * parseFloat(item.rate || 0))
                    }));

                    setPoData({
                        id: data.id,
                        poNumber: data.poNumber,
                        date: data.date,
                        validTill: data.tillDate || data.validTill,
                        particular: data.particular,
                        status: data.status || 'Pending',
                        grandTotal: data.grandTotal || 0,
                        project: {
                            name: project.projectName || project.name,
                            id: project.id
                        },
                        vendor: {
                            vendorName: vendorObj.vendorName || 'N/A',
                            companyName: vendorObj.companyName,
                            phone: vendorObj.whatsAppNumber || vendorObj.phone,
                            email: vendorObj.email
                        },
                        items: formattedItems
                    });
                } else {
                    Swal.fire('Error', 'Purchase Order not found.', 'error');
                    navigate(-1);
                }
            } catch (error) {
                console.error('Error fetching PO details:', error);
                Swal.fire('Error', 'Failed to load PO details.', 'error');
                navigate(-1);
            } finally {
                setLoading(false);
            }
        };

        if (poId) {
            fetchPODetails();
        }
    }, [poId, navigate]);

    const formatCurrency = (amount) => {
        return `₹${parseFloat(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className={styles['po-detail-container']} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Loader text="Loading purchase order details..." />
            </div>
        );
    }

    if (!poData) return null;

    return (
        <div className={styles['po-detail-container']}>
            <div className={styles['page-header']}>
                <button onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/po-management')} className="back-btn">
                    <MdArrowBack size={20} />
                </button>
                <div className={styles['po-title-section']}>
                    <h1>
                        #{poData.poNumber}
                        <span className={`${styles['status-badge']} ${styles[`status-${poData.status.toLowerCase()}`] || styles['status-pending']}`}>
                            {poData.status}
                        </span>
                    </h1>
                    <div className={styles['po-meta-info']}>
                        Project: {poData.project?.name || 'N/A'} • Created on {formatDate(poData.date)}
                    </div>
                </div>
            </div>

            <div className={styles['detail-grid']}>
                <div className={styles['main-content']}>
                    {/* Vendor & Items */}
                    <div className={styles['card']}>
                        <div className={styles['card-header']}>
                            <h3>Vendor: {poData.vendor.vendorName}</h3>
                            {poData.vendor.companyName && <span style={{ fontSize: '13px', color: '#6b7280' }}>{poData.vendor.companyName}</span>}
                        </div>
                        <div className={styles['card-content']}>
                            <div className={styles['vendor-grid']}>
                                <div className={styles['vendor-icon']}>
                                    <MdBusiness size={28} />
                                </div>
                                <div className={styles['vendor-info']}>
                                    <div className={styles['contact-list']}>
                                        {poData.vendor.email && (
                                            <div className={styles['contact-item']}>
                                                <MdEmail size={16} /> {poData.vendor.email}
                                            </div>
                                        )}
                                        {poData.vendor.phone && (
                                            <div className={styles['contact-item']}>
                                                <MdPhone size={16} /> {poData.vendor.phone}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className={styles['table-container']} style={{ marginTop: '24px' }}>
                                <table className={styles['items-table']}>
                                    <thead>
                                        <tr>
                                            <th>Item & Description</th>
                                            <th style={{ textAlign: 'center' }}>Qty</th>
                                            <th>Size</th>
                                            <th>Unit</th>
                                            <th>Rate</th>
                                            <th style={{ textAlign: 'right' }}>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {poData.items.map((item, iIdx) => (
                                            <tr key={item.id || iIdx}>
                                                <td>
                                                    <span className={styles['item-name']}>{item.productName}</span>
                                                    {item.description && (
                                                        <span className={styles['item-desc']}>{item.description}</span>
                                                    )}
                                                </td>
                                                <td style={{ textAlign: 'center' }}>{item.qty}</td>
                                                <td style={{ textAlign: 'center' }}>{item.size || 'N/A'}</td>
                                                <td>{item.unit}</td>
                                                <td>{formatCurrency(item.rate)}</td>
                                                <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(item.total)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className={styles['mobile-items-view']}>
                                {poData.items.map((item, index) => (
                                    <div key={item.id || index} className={styles['mobile-item-card']}>
                                        <div className={styles['mobile-item-header']}>
                                            <div className={styles['mobile-item-title']}>
                                                <div className={styles['sr-badge']}>0{index + 1}</div>
                                                <strong>{item.productName}</strong>
                                            </div>
                                        </div>
                                        <div className={styles['mobile-item-body']}>
                                            {item.description && (
                                                <div className={`${styles['mobile-item-row']} ${styles['full-width']}`}>
                                                    <span className={styles['m-label']}>Description</span>
                                                    <span className={styles['m-value']}>{item.description}</span>
                                                </div>
                                            )}
                                            <div className={styles['mobile-item-row']}>
                                                <span className={styles['m-label']}>Qty</span>
                                                <span className={styles['m-value']}>{item.qty} {item.unit}</span>
                                            </div>
                                            <div className={styles['mobile-item-row']}>
                                                <span className={styles['m-label']}>Size</span>
                                                <span className={styles['m-value']}>{item.size || 'N/A'}</span>
                                            </div>
                                            <div className={styles['mobile-item-row']}>
                                                <span className={styles['m-label']}>Rate</span>
                                                <span className={styles['m-value']}>{formatCurrency(item.rate)}</span>
                                            </div>
                                            <div className={styles['mobile-item-row']}>
                                                <span className={styles['m-label']}>Total</span>
                                                <span className={styles['m-value']} style={{ color: '#4f46e5' }}>{formatCurrency(item.total)}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles['sidebar']}>
                    <div className={styles['card']}>
                        <div className={styles['card-header']}>
                            <h3>Summary</h3>
                        </div>
                        <div className={styles['card-content']}>
                            <div className={styles['info-item']}>
                                <span className={styles['info-label']}>PO Number</span>
                                <span className={styles['info-value']}>{poData.poNumber}</span>
                            </div>
                            <div className={styles['info-item']}>
                                <span className={styles['info-label']}>Date</span>
                                <span className={styles['info-value']}>
                                    <MdCalendarToday size={16} /> {formatDate(poData.date)}
                                </span>
                            </div>
                            <div className={styles['info-item']}>
                                <span className={styles['info-label']}>Valid Till</span>
                                <span className={styles['info-value']}>
                                    <MdCalendarToday size={16} /> {formatDate(poData.validTill)}
                                </span>
                            </div>
                            {poData.particular && (
                                <div className={styles['info-item']}>
                                    <span className={styles['info-label']}>Particular</span>
                                    <span className={styles['info-value']}>{poData.particular}</span>
                                </div>
                            )}
                            <div className={styles['summary-row']} style={{ marginTop: '24px', borderTop: '1px solid #eee', paddingTop: '16px' }}>
                                <span>Grand Total</span>
                                <span style={{ fontWeight: 700, fontSize: '18px', color: '#4f46e5' }}>{formatCurrency(poData.grandTotal)}</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles['card']}>
                        <div className={styles['card-header']}>
                            <h3>Project Information</h3>
                        </div>
                        <div className={styles['card-content']}>
                            <div className={styles['info-item']}>
                                <span className={styles['info-label']}>Project Name</span>
                                <span className={styles['info-value']}>{poData.project?.name || 'N/A'}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewPODetail;
